import React from 'react'
import Box from '../Box'
import styled from '@emotion/styled'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { ActivityRegistrationStatus, Client, GroupByOption } from '../../../../pure-js/types/GrooverTypes'
import { resolveActivityPageFieldVisibility } from '../../../../pure-js/libs/clientHelper'
import { useCurrency } from '../../hooks/useCurrency'
import { formatPrice, getLocalizedActivityType } from '../../utils/activityUtils'
import AccessTimeIcon from '@mui/icons-material/AccessTime'
import LocationOnIcon from '@mui/icons-material/LocationOn'
import RoutePath from '../../../../pure-js/libs/RoutePath'
import { buildRoute } from '../../../../pure-js/libs/RouteBuilder'
import {
  WEEKDAYS,
  STYLE_COLORS,
  getWeekdayIndex,
  timeToMinutes,
  darkenColor,
  getActivityDisplayStatus,
  getContrastTextColor
} from './ViewHelper'
import { getThemeConfig } from '../../themeConfig'

const WeekViewGrid = styled.div<{ $columns: number }>`
  display: grid;
  grid-template-columns: repeat(${(props) => props.$columns}, 1fr);
  grid-auto-rows: min-content;
  box-sizing: border-box;
  width: 100vw;
  max-width: 100vw;
  margin-left: calc(-50vw + 50%);
  padding: 0 24px;

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
    padding: 0 8px;
  }
`

const WeekViewDayHeader = styled.div<{ $backgroundColor: string; $textColor: string }>`
  padding: 12px 8px 4px 8px;
  background: ${(props) => props.$backgroundColor};
  font-weight: 600;
  text-align: center;
  color: ${(props) => props.$textColor};
  letter-spacing: 0.5px;
  font-size: 1.05em;
  position: sticky;
  top: 0;
  z-index: 2;
  border-right: 1px solid
    ${() => {
      const theme = getThemeConfig()
      const isDark =
        theme.backgroundColor === '#1a1a1a' ||
        theme.backgroundColor === '#2d2d2d' ||
        theme.backgroundColor === '#000000' ||
        theme.backgroundColor === '#1a1a2e'
      return isDark ? 'rgba(255,255,255,0.2)' : '#e0e0e0'
    }};

  @media (max-width: 600px) {
    display: none;
  }
`

const WeekViewLocationHeader = styled.div`
  grid-column: 1 / -1;
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.85rem;
  font-weight: 700;
  color: ${() => {
    const theme = getThemeConfig()
    return theme.textColor || '#333'
  }};
  padding: 12px 10px 6px 10px;
  border-bottom: 1px solid
    ${() => {
      const theme = getThemeConfig()
      const isDark =
        theme.backgroundColor === '#1a1a1a' ||
        theme.backgroundColor === '#2d2d2d' ||
        theme.backgroundColor === '#000000' ||
        theme.backgroundColor === '#1a1a2e'
      return isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.08)'
    }};
  margin-top: 8px;
`

const WeekViewDayCell = styled.div<{ $borderColor: string; $gridColumn: number }>`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 8px;
  border-right: 1px solid ${(props) => props.$borderColor};
  min-height: 40px;
  height: fit-content;
  grid-column: ${(props) => props.$gridColumn};

  @media (max-width: 600px) {
    grid-column: 1;
    border-right: none;
    border-bottom: 1px solid ${(props) => props.$borderColor};
    padding-bottom: 16px;
  }
`

const MobileDayLabel = styled.div`
  display: none;
  font-size: 0.8rem;
  font-weight: 600;
  letter-spacing: 0.4px;
  color: ${() => {
    const theme = getThemeConfig()
    return theme.textColor || '#444'
  }};
  opacity: 0.6;
  padding-bottom: 2px;

  @media (max-width: 600px) {
    display: block;
  }
`

const DaySubtitle = styled.div`
  font-size: 0.95em;
  color: ${() => {
    const theme = getThemeConfig()
    const isDark =
      theme.backgroundColor === '#1a1a1a' ||
      theme.backgroundColor === '#2d2d2d' ||
      theme.backgroundColor === '#000000' ||
      theme.backgroundColor === '#1a1a2e'
    // Use a slightly lighter version of the text color for subtitle
    return isDark ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)'
  }};
  font-weight: 400;
  margin-bottom: 4px;
`

const ActivityBoxWrapper = styled(Box)`
  position: relative;
  flex: 1 1 0;
  min-width: 0;
  @media (max-width: 768px) {
    width: 100%;
  }
  &:hover > .activity-tooltip {
    opacity: 1;
    pointer-events: auto;
  }
`

const RedesignedActivityBox = styled(Box)<{ stylecolor: string }>`
  background: ${(props) => props.stylecolor};
  box-shadow: 0 1px 8px rgba(0, 0, 0, 0.1);
  padding: 16px 12px 12px 12px;
  border-radius: 4px;
  cursor: pointer;
  position: relative;
  margin: 2px;
  display: flex;
  flex-direction: column;
  min-height: 110px;
  width: 100%;
  transition:
    box-shadow 0.2s,
    background 0.2s;
  &:hover {
    background: ${(props) => darkenColor(props.stylecolor)};
    box-shadow: 0 4px 16px rgba(0, 120, 255, 0.15);
  }

  svg {
    color: inherit;
    fill: currentColor;
  }
`

const TimePill = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: rgba(0, 0, 0, 0.18);
  font-size: 1.05em;
  font-weight: 600;
  padding: 2px 12px;
  border-radius: 16px;
  margin-bottom: 6px;
  letter-spacing: 1px;
  line-height: 1;

  svg {
    color: inherit;
    fill: currentColor;
  }
`

const ActivityName = styled(Box)`
  font-size: 0.9em;
  color: #222;
  word-break: break-word;
`

const StatusSection = styled.div<{ bg: string; color?: string }>`
  width: fit-content;
  min-width: 0;
  background: ${(props) => props.bg};
  color: ${(props) => props.color || '#fff'};
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  font-size: 0.85em;
  font-weight: 600;
  border-radius: 16px;
  padding: 2px 14px;
  min-height: 24px;
  position: absolute;
  bottom: 8px;
  left: -10px;
  z-index: 3;
  box-shadow:
    0 4px 16px rgba(0, 0, 0, 0.18),
    0 1.5px 4px rgba(0, 0, 0, 0.12);
  /* Prevent color change on hover */
  transition: none;
  &,
  * {
    color: ${(props) => props.color || '#fff'} !important;
    fill: ${(props) => props.color || '#fff'} !important;
  }
`

const ActivityWeekView: React.FC<{
  activities: {
    id: string
    name: string
    startDate: string
    time?: string
    style?: string
    type?: string
    length?: number
    level?: string
    bannerUrl?: string
    registrationStatus?: ActivityRegistrationStatus
    dropinOnly?: boolean
    instructorsNames?: string
    place?: string
    city?: string
    locationId?: string
    price?: number
    maxAllowedParticipants?: number
    seoLink?: string
    occations?: { startDate: string; cancelled?: boolean }[]
  }[]
  client?: Client
  fields?: string[]
  groupBy?: GroupByOption
}> = ({ activities, client, fields, groupBy }) => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const lang = searchParams.get('lang')
  const langQuery = lang ? { lang } : undefined
  const theme = getThemeConfig()
  const currency = useCurrency(client)
  const fieldVisibility = resolveActivityPageFieldVisibility(client)

  // Determine if we're in dark mode
  const isDark =
    theme.backgroundColor === '#1a1a1a' ||
    theme.backgroundColor === '#2d2d2d' ||
    theme.backgroundColor === '#000000' ||
    theme.backgroundColor === '#1a1a2e'

  // Get theme colors
  const headerBackground = isDark ? 'rgba(255, 255, 255, 0.05)' : '#fff'
  const headerTextColor = theme.textColor || (isDark ? '#fff' : '#222')
  const borderColor = theme.borderColor || (isDark ? 'rgba(255, 255, 255, 0.2)' : '#e0e0e0')

  // Resolve the display location name for an activity (used as fallback)
  const resolveLocation = (activity: (typeof activities)[0]): string => {
    if (activity.locationId && client?.locations) {
      const match = client.locations.find((l) => l.id === activity.locationId)
      if (match?.name) return match.name
    }
    return activity.city || ''
  }

  // Resolve the outer group key based on the configured groupBy option
  const resolveGroupKey = (activity: (typeof activities)[0]): string => {
    switch (groupBy) {
      case GroupByOption.Style:
        return activity.style || ''
      case GroupByOption.Type:
        return getLocalizedActivityType(activity.type || '', client as Client)
      case GroupByOption.City:
        return activity.city || ''
      case GroupByOption.Instructors:
        return activity.instructorsNames || ''
      case GroupByOption.Level:
        return activity.level || ''
      case GroupByOption.None:
        return ''
      case GroupByOption.Location:
        return resolveLocation(activity)
      default:
        return resolveLocation(activity)
    }
  }

  // Show the location pin icon only when grouping is location/city-based
  const showLocationIcon =
    !groupBy ||
    groupBy === GroupByOption.Location ||
    groupBy === GroupByOption.City ||
    groupBy === GroupByOption.StartDate ||
    groupBy === GroupByOption.Period

  // Group activities by outer group key first, then by weekday within each group
  const groupOrder: string[] = []
  const byGroupAndDay = new Map<string, Map<number, typeof activities>>()

  activities.forEach((activity) => {
    if (!activity.startDate) return
    const groupKey = resolveGroupKey(activity)

    if (!byGroupAndDay.has(groupKey)) {
      byGroupAndDay.set(groupKey, new Map())
      groupOrder.push(groupKey)
    }
    const dayMap = byGroupAndDay.get(groupKey)!

    const weekdaySet = new Set<number>()
    if (activity.occations && activity.occations.length > 0) {
      activity.occations.forEach((o) => {
        if (!o.cancelled) weekdaySet.add(getWeekdayIndex(o.startDate))
      })
    }
    if (weekdaySet.size === 0) weekdaySet.add(getWeekdayIndex(activity.startDate))

    weekdaySet.forEach((dayIndex) => {
      if (!dayMap.has(dayIndex)) dayMap.set(dayIndex, [])
      const bucket = dayMap.get(dayIndex)!
      if (!bucket.find((a) => a.id === activity.id)) bucket.push(activity)
    })
  })

  // Returns the time from the earliest non-cancelled occasion on the given weekday,
  // falling back to activity.time when no matching occasion exists.
  const getOccasionTimeForDay = (activity: (typeof activities)[0], dayIndex: number): string | undefined => {
    if (activity.occations && activity.occations.length > 0) {
      const first = activity.occations
        .filter((o) => !o.cancelled && getWeekdayIndex(o.startDate) === dayIndex)
        .sort((a, b) => a.startDate.localeCompare(b.startDate))[0]
      if (first) {
        // startDate is stored as "yyyy-MM-dd'T'HH:mm" (ISO) or legacy "yyyy-MM-dd HH:mm"
        const timePart = first.startDate.split(/[T ]/)[1]
        if (timePart) return timePart
      }
    }
    return activity.time
  }

  // Sort activities by their occasion time for each specific day column
  byGroupAndDay.forEach((dayMap, _loc) => {
    dayMap.forEach((bucket, dayIndex) => {
      bucket.sort((a, b) => {
        const tA = getOccasionTimeForDay(a, dayIndex)
        const tB = getOccasionTimeForDay(b, dayIndex)
        if (!tA) return 1
        if (!tB) return -1
        return timeToMinutes(tA) - timeToMinutes(tB)
      })
    })
  })

  // Active weekday indexes: days that have at least one activity across all groups
  const activeWeekdayIndexes = WEEKDAYS.map((_, idx) => idx).filter((dayIdx) =>
    Array.from(byGroupAndDay.values()).some((dayMap) => (dayMap.get(dayIdx)?.length ?? 0) > 0)
  )

  const showGroupHeaders = groupOrder.length > 1 || (groupOrder.length === 1 && groupOrder[0] !== '')

  // Helper to get style color from client.themeConfig.danceStyles or fallback
  const getStyleColor = (style?: string) => {
    if (!style) return STYLE_COLORS.default
    const styleKey = style.toLowerCase()
    return (
      client?.themeConfig?.danceStyles?.find((ds) => ds.name.toLowerCase() === styleKey)?.color ||
      STYLE_COLORS[styleKey] ||
      STYLE_COLORS.default
    )
  }

  return (
    <WeekViewGrid $columns={Math.max(activeWeekdayIndexes.length, 1)}>
      {/* Day headers row */}
      {activeWeekdayIndexes.map((dayIndex) => {
        const uniqueCount = new Set(
          Array.from(byGroupAndDay.values()).flatMap((dayMap) => (dayMap.get(dayIndex) ?? []).map((a) => a.id))
        ).size
        return (
          <WeekViewDayHeader key={dayIndex} $backgroundColor={headerBackground} $textColor={headerTextColor}>
            {WEEKDAYS[dayIndex]}
            <DaySubtitle>{uniqueCount} klasser</DaySubtitle>
          </WeekViewDayHeader>
        )
      })}

      {/* One section per group */}
      {groupOrder.map((groupKey) => {
        const dayMap = byGroupAndDay.get(groupKey)!
        return (
          <React.Fragment key={groupKey || '__none__'}>
            {/* Full-width group header spanning all day columns */}
            {showGroupHeaders && groupKey && (
              <WeekViewLocationHeader>
                {showLocationIcon && <LocationOnIcon sx={{ fontSize: '1rem' }} />}
                {groupKey}
              </WeekViewLocationHeader>
            )}

            {/* Only days that have activities in this group (skip empty day rows/cells) */}
            {activeWeekdayIndexes.map((dayIndex, columnIndex) => {
              const dayActivities = dayMap.get(dayIndex) ?? []
              if (dayActivities.length === 0) return null
              return (
                <WeekViewDayCell key={dayIndex} $borderColor={borderColor} $gridColumn={columnIndex + 1}>
                  <MobileDayLabel>{WEEKDAYS[dayIndex]}</MobileDayLabel>
                  {dayActivities.map((activity) => {
                    const styleColor = getStyleColor(activity.style)
                    const textColor = getContrastTextColor(styleColor)
                    // Empty fields=[] means "use defaults" (same as ActivityCard compact/list)
                    const rawFields = fields && fields.length > 0 ? fields : null
                    const showPlace = rawFields
                      ? rawFields.includes('room') && !!activity.place
                      : fieldVisibility.showRoom && !!activity.place
                    const showPriceValue = rawFields
                      ? rawFields.includes('price') && activity.price !== undefined
                      : fieldVisibility.showPrice && activity.price !== undefined
                    const showInstructors = rawFields
                      ? rawFields.includes('instructors')
                      : fieldVisibility.showInstructors
                    const showTime = rawFields ? rawFields.includes('time') : true
                    const showStyle = rawFields ? rawFields.includes('style') : true
                    const showLevel = rawFields ? rawFields.includes('level') : true
                    const displayStatus = getActivityDisplayStatus(activity)
                    const occasionTime = getOccasionTimeForDay(activity, dayIndex)
                    return (
                      <ActivityBoxWrapper
                        key={activity.id}
                        fullWidth
                        style={{ cursor: 'pointer' }}
                        onClick={() => {
                          const path =
                            client?.slug && activity.seoLink
                              ? buildRoute(
                                  RoutePath.ACTIVITY_DETAIL_SEO,
                                  { clientSlug: client.slug, activitySlug: activity.seoLink },
                                  langQuery
                                )
                              : buildRoute(RoutePath.ACTIVITY_DETAIL, { activityId: activity.id }, langQuery)
                          if (client?.openActivityInNewTab) {
                            window.open(path, '_blank')
                            return
                          }
                          navigate(path, { state: { from: 'PublicActivitiesPage' } })
                        }}
                      >
                        <RedesignedActivityBox
                          stylecolor={styleColor}
                          direction="column"
                          align="flex-start"
                          style={{ position: 'relative', color: textColor }}
                          className="activity-box"
                        >
                          {showTime && occasionTime && (
                            <div
                              style={{
                                display: 'flex',
                                justifyContent: 'flex-start',
                                alignItems: 'center',
                                width: '100%'
                              }}
                            >
                              <TimePill
                                className="time-pill"
                                style={{
                                  color: textColor,
                                  background: textColor === '#fff' ? 'rgba(255,255,255,0.18)' : 'rgba(0,0,0,0.10)'
                                }}
                              >
                                <AccessTimeIcon style={{ fontSize: 16, display: 'block', color: textColor }} />
                                {occasionTime}
                              </TimePill>
                            </div>
                          )}
                          {(displayStatus?.text || displayStatus?.icon) && (
                            <StatusSection className="no-hover-color" bg={displayStatus.bg} color={displayStatus.color}>
                              {displayStatus.icon}
                              {displayStatus.text}
                            </StatusSection>
                          )}
                          <ActivityName
                            style={{
                              fontWeight: 700,
                              fontSize: '1.08em',
                              color: textColor,
                              margin: '6px 0 2px 0',
                              textAlign: 'left',
                              lineHeight: 1.2
                            }}
                          >
                            {activity.name}
                          </ActivityName>
                          {(showStyle && activity.style) || (showLevel && activity.level) ? (
                            <div style={{ fontSize: '0.85em', color: textColor, marginBottom: 2, opacity: 0.9 }}>
                              {showStyle && activity.style && <span>{activity.style}</span>}
                              {showStyle && activity.style && showLevel && activity.level && <span> · </span>}
                              {showLevel && activity.level && <span>{activity.level}</span>}
                            </div>
                          ) : null}
                          {showInstructors && activity.instructorsNames && (
                            <ActivityName
                              style={{ fontSize: '0.90em', color: textColor, marginBottom: 2, textAlign: 'left' }}
                            >
                              {activity.instructorsNames}
                            </ActivityName>
                          )}
                          {(showPlace || showPriceValue) && (
                            <div style={{ fontSize: '0.92em', color: textColor, marginTop: 4, opacity: 0.8 }}>
                              {showPlace && <span>{activity.place}</span>}
                              {showPlace && showPriceValue && <span> · </span>}
                              {showPriceValue && <span>{formatPrice(activity.price ?? 0, currency)}</span>}
                            </div>
                          )}
                        </RedesignedActivityBox>
                      </ActivityBoxWrapper>
                    )
                  })}
                </WeekViewDayCell>
              )
            })}
          </React.Fragment>
        )
      })}
    </WeekViewGrid>
  )
}

export default ActivityWeekView
